// components
import maEventBus from '@/components/eventbus'
import maAccordion from '@/components/accordion'
import maList from '@/components/list'
import maModal from '@/components/modal'
import maInput from '@/components/input'
import maForm from '@/components/form'
import maInputBtn from '@/components/input/modules/btn'
import maRange from '@/components/range'
import maDamageSummary from './components/damageSummary'
import maMaterialSummary from './components/materialSummary'
import maLoader from '@/components/loader'
import maInputRadio from '@/components/input/modules/radio'

// utils
import downloadBlobFile from '@/utils/downloadBlobFile'
import debounce from '@/utils/debounce'

// vuex
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'

// types
import {
  WARRANTY_MODULE_NAME,
  WARRANTY_DELETE_DAMAGE,
  WARRANTY_GET_REPORT,
  WARRANTY_GET_MATERIAL_SUMMARY,
  WARRANTY_UPDATE_REPAIR_TYPE,
  UPDATE_LOADER,
  USER_MODULE_NAME
} from '@/store/types'

// constants
import { ALLIANZ_ITALY_USER } from '@/constants'

// api
import {
  apiGetPdfReport,
  apiGetPdfReportFnol,
  apiGetSmartReport
} from '@/services/API/pdf'
import downloadExcelFile from '@/utils/downloadExcelFile'
/**
 * @module
 * @name warrantySticky
 * @description Sticky bar within warrantyLayout.
 */
export default {
  name: 'warrantySticky',

  /**
   * @property {Boolean} expanded If true, sticky simulates fullscreen modal.
   * @property {Object} materialSummary Summary got from backend which contains
   *                                     materialSummary info.
   */
  data: function() {
    return {
      expanded: false,
      materialSummary: {}
    }
  },

  components: {
    maAccordion,
    maModal,
    maList,
    maInput,
    maForm,
    maRange,
    maDamageSummary,
    maMaterialSummary,
    maLoader,
    maInputRadio,
    maInputBtn
  },

  computed: {
    ...mapState(WARRANTY_MODULE_NAME, {
      stateWarrantySinisterId: 'sinisterId',
      stateWarrantySinisterSummary: 'sinisterSummary',
      stateWarrantyRepairType: 'repairType'
    }),
    ...mapGetters(WARRANTY_MODULE_NAME, {
      getWarrantySinisterDamagesCount: 'sinisterDamagesCount'
    }),
    ...mapState(USER_MODULE_NAME, {
      stateUserRole: 'role'
    }),
    shouldCloseAccordion: function() {
      return this.FNOLFlow.params.closeAccordions
        ? this.FNOLFlow.params.closeAccordions
        : false
    },
    shouldHideSummarize: function() {
      return this.FNOLFlow.params.hideSummary
        ? this.FNOLFlow.params.hideSummary
        : false
    },
    externalDamageId: function() {
      return this.FNOLFlow.params.externalDamageId
        ? this.FNOLFlow.params.externalDamageId
        : null
    },
    hasExternalDamageId: function() {
      return this.FNOLFlow.params.externalDamageId !== null
    },
    hasWarrantyRepairTypeNotSelected: function() {
      return this.stateWarrantyRepairType === null
    },
    isAllianzITUSer: function() {
      return this.stateUserRole === ALLIANZ_ITALY_USER
    }
  },

  methods: {
    ...mapActions(WARRANTY_MODULE_NAME, {
      actWarrantyDeleteDamage: WARRANTY_DELETE_DAMAGE,
      actWarrantyGetReport: WARRANTY_GET_REPORT,
      actWarrantySinisterUpdateMaterialSummary: WARRANTY_GET_MATERIAL_SUMMARY
    }),

    ...mapMutations({
      updateLoader: UPDATE_LOADER
    }),

    ...mapMutations(WARRANTY_MODULE_NAME, {
      mutWarrantyUpdateRepairType: WARRANTY_UPDATE_REPAIR_TYPE
    }),

    /**
     * @function
     * @name triggerAnalysis
     * @description It requests analysis from backend and shows it expanding
     *              sticky to fullscreen modal.
     */
    triggerAnalysis() {
      this.$emit('showSummary')
      this.actWarrantyGetReport()
        .then(data => {
          if (this.FNOLFlow.isExecuting) {
            window.parent.postMessage(data, '*')
          }
          maEventBus.$emit('summary:expand')
        })
        .catch(() => {
          maEventBus.$emit('summary:collapse')
          this.throwNotification(
            this.$t('notifications.error.title'),
            this.$t('notifications.error.body'),
            'KO'
          )
        })

      if (!this.expanded && !this.shouldHideSummarize) {
        this.triggerExpand()
        this.closeAccordion()
      }
    },

    /**
     * @function
     * @name triggerPdfReport
     * @description Download PDF Summary
     */
    triggerPdfReport() {
      this.updateLoader(true)

      apiGetPdfReport(
        this.stateWarrantySinisterId,
        this.stateWarrantyRepairType
      )
        .then(data => {
          downloadBlobFile(data, 'MAValue summary.pdf')
          this.throwNotification(
            this.$t('notifications.pdfDamages.title'),
            this.$t('notifications.pdfDamages.body'),
            'OK'
          )
        })
        .catch(err => {
          console.debug('err', err)
          this.throwNotification(
            this.$t('notifications.error.title'),
            this.$t('notifications.error.body'),
            'KO'
          )
        })
        .finally(() => {
          this.updateLoader(false)
        })
    },
    /**
     * @function
     * @name triggerPdfReportFnol
     * @description Download PDF Summary when Fnol is executing
     */
    triggerPdfReportFnol() {
      this.updateLoader(true)
      apiGetPdfReportFnol(this.stateWarrantySinisterId)
        .then(data => {
          downloadBlobFile(data, `${this.stateWarrantySinisterId} dommages.pdf`)
          this.throwNotification(
            this.$t('notifications.pdfDamages.title'),
            this.$t('notifications.pdfDamages.body'),
            'OK'
          )
        })
        .catch(err => {
          console.debug('err', err)
          this.throwNotification(
            this.$t('notifications.error.title'),
            this.$t('notifications.error.body'),
            'KO'
          )
        })
        .finally(() => {
          this.updateLoader(false)
        })
    },

    /**
     * @function
     * @name triggerExpand
     * @description Toggle expanded
     */
    triggerExpand() {
      this.expanded = !this.expanded
    },

    /**
     * @function
     * @name closeAccordion
     * @description Hide accordion content
     *
     */
    closeAccordion() {
      if (this.$refs.warrantyStickyAccordion) {
        this.$refs.warrantyStickyAccordion.hideContent()
        this.$refs.warrantyStickyAccordion.$data.visible = false
      }
    },

    /**
     * @function
     * @name openModal
     * @param {String} ref Vue object reference name.
     * @description Open modal using vue ref, passing as parameter.
     */
    openModal(ref) {
      this.$refs[ref].$data.showModal = true
    },

    /**
     * @function
     * @name triggerMaterialList
     * @description Requests material info from backend, and if it goes well,
     *              it's rendered in a modal.
     */
    triggerMaterialList: debounce(function() {
      this.updateLoader(true)
      this.actWarrantySinisterUpdateMaterialSummary()
        .then(() => {
          this.$refs.materialList.$data.showModal = true
        })
        .catch(() => {
          this.throwNotification(
            this.$t('notifications.error.title'),
            this.$t('notifications.error.body'),
            'KO'
          )
        })
        .finally(() => {
          this.updateLoader(false)
        })
    }, 100),

    /**
     * @function
     * @name handleRemoveDamage
     * @param {String} damageId Damage's id to remove from backend.
     * @description Request to delete damage from backend. If the damage is removed
     *              a notification is showed and all lists containing related data
     *              are updated in the action.
     */
    handleRemoveDamage: function(damageId) {
      const that = this
      this.actWarrantyDeleteDamage(damageId)
        .then(() => {
          maEventBus.$emit('damage:remove', damageId)
          that.throwNotification(
            that.$i18n.t('notifications.deleteDamage.title'),
            this.$t('notifications.deleteDamage.body'),
            'OK'
          )
          if (that.getWarrantySinisterDamagesCount <= 1) {
            maEventBus.$emit('summary:collapse')
          }
        })
        .catch(() => {
          this.throwNotification(
            this.$t('notifications.error.title'),
            this.$t('notifications.error.body'),
            'KO'
          )
        })
    },

    /**
     * @function
     * @name checkIfItemCanDelete
     * @param {Object} item Item added to the damage list.
     * @description Checks if the element in the damage list can be deleted. If there is a
     *              externalDamageId means that the user is in a iframe and externalDamageId
     *              has beed added by url. Also, item needs its value can_delete to be able
     *              to be deleted.
     */
    checkIfItemCanDelete: function(item) {
      if (this.hasExternalDamageId) {
        return (
          item.can_delete && item.external_damage_id === this.externalDamageId
        )
      } else {
        return item.can_delete
      }
    },

    downloadSmartReport() {
      apiGetSmartReport(this.stateWarrantySinisterId)
        .then(data => {
          downloadExcelFile(data.url)
        })
        .catch(err => {
          console.debug('err', err)
          this.throwNotification(
            this.$t('notifications.error.title'),
            this.$t('notifications.error.body'),
            'KO'
          )
        })
    }
  },

  mounted() {
    maEventBus.$on(
      'openPriceModal',
      /**
       * @event
       * @name openPriceModal
       * @description Open manpower set modal.
       */
      function() {
        this.$refs.priceModal.$data.showModal = true
      }.bind(this)
    )

    maEventBus.$on(
      'summary:collapse',
      /**
       * @event
       * @name summary:collapse
       * @description Collapse sticky accordion.
       */
      function() {
        this.expanded = false
      }.bind(this)
    )

    maEventBus.$on(
      'damage:closeAccordion',
      /**
       * @event
       * @name damage:closeAccordion
       * @description Close accodion with a 3s. delay.
       */
      function() {
        setTimeout(() => {
          this.closeAccordion()
        }, 3000)
      }.bind(this)
    )
  },

  /**
   * @property {function} stateWarrantySinisterSummary Resize accordion
   *  depending on the number of elements the accordion contains.
   */
  watch: {
    stateWarrantySinisterSummary: function(newSummary, oldSummary) {
      if (this.getWarrantySinisterDamagesCount <= 1) {
        setTimeout(() => {
          if (!this.shouldCloseAccordion) {
            maEventBus.$emit('accordion:resize')
          }
        }, 300)
      } else if (this.$refs.warrantyStickyAccordion) {
        this.$refs.warrantyStickyAccordion.resizeContent()
      }
    }
  }
}
